/**
 * DeckSummaryView - Text-based multi-column deck summary
 *
 * Displays deck contents grouped by the user's selected "Group by" preference
 * in a compact, scannable format inspired by tools like Archidekt and TappedOut.
 * Uses masonry-style column assignment to balance section heights across columns.
 * Zone sections (sideboard, maybeboard) are shown as flat sections below mainboard.
 */
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import { useLocalStorage, useWindowSize } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

import { formatPrice } from '@/lib/price';
import type { Currency } from '@/types/card';

import { getCmcLabel, getColorIdentityGroup, getColorIdentitySortOrder, getTypeSortOrder } from '@/lib/cardGrouping';
import { getCmc, getOracleId, TYPE_CATEGORY_LABELS } from '@/lib/cardUtils';

import { type DeckGroupBy, deckGroupByAtom } from '../state/deckEditorSettings';
import type { EnrichedDeckCard } from '../types';
import SummarySection, { type SummarySectionCard } from './SummarySection';

// ============================================================================
// Types
// ============================================================================

export interface DeckSummaryViewProps {
  /** Cards in the main deck (excluding commander) */
  cards: EnrichedDeckCard[];
  /** Commander cards (displayed prominently) */
  commanderCards?: EnrichedDeckCard[];
  /** Sideboard cards */
  sideboardCards?: EnrichedDeckCard[];
  /** Maybeboard cards */
  maybeboardCards?: EnrichedDeckCard[];
  /** Tokens and emblems */
  tokensCards?: EnrichedDeckCard[];
  /** Called when a card is clicked */
  onCardClick?: (scryfallId: string, deckCardId: string) => void;
  /** Map of oracle IDs the user owns (for owned highlighting) */
  ownedOracleIds?: Set<string>;
  /** Map of scryfall IDs to prices (single copy USD) */
  priceMap?: Map<string, number>;
  /** Currency for price display */
  currency?: Currency;
}

/** Category definition with sorting metadata */
interface CategoryGroup {
  id: string;
  title: string;
  cards: SummarySectionCard[];
  count: number;
  sortOrder: number;
  /** Whether this is a special zone section below the main deck */
  isZoneSection?: boolean;
}

/** User's sort preference for cards within sections */
export type SummarySortBy = 'cmc' | 'name' | 'price';

// ============================================================================
// Masonry Column Distribution
// ============================================================================

/**
 * Distribute categories across columns using a "shortest column" strategy.
 * Each category is assigned to whichever column currently has the least total height.
 * Height is estimated as number of cards + 1 (for the header).
 */
function distributeToColumns(categories: CategoryGroup[], columnCount: number): CategoryGroup[][] {
  const columns: CategoryGroup[][] = Array.from({ length: columnCount }, () => []);
  const columnHeights: number[] = Array(columnCount).fill(0);

  for (const cat of categories) {
    // Find the shortest column
    let shortestIdx = 0;
    for (let i = 1; i < columnCount; i++) {
      if (columnHeights[i] < columnHeights[shortestIdx]) {
        shortestIdx = i;
      }
    }

    // Add category to shortest column
    columns[shortestIdx].push(cat);
    // Estimate height: header (1) + cards count
    columnHeights[shortestIdx] += 1 + cat.cards.length;
  }

  return columns;
}

// ============================================================================
// Category Grouping Logic
// ============================================================================

/** Zone section configs (not affected by groupBy) */
const ZONE_SECTION_CONFIG: Record<string, { title: string; sortOrder: number }> = {
  commander: { title: 'Commander', sortOrder: 0 },
  companion: { title: 'Companion', sortOrder: 1 },
  sideboard: { title: 'Sideboard', sortOrder: 100 },
  maybeboard: { title: 'Maybeboard', sortOrder: 101 },
  tokens: { title: 'Tokens & Emblems', sortOrder: 102 },
};

/** Labels for groupBy modes */
const GROUP_BY_LABELS: Record<DeckGroupBy, string> = {
  type: 'Type',
  cmc: 'CMC',
  color_identity: 'Color',
};

/**
 * Sort cards within a category based on the groupBy mode
 */
function sortCardsInGroup(cards: SummarySectionCard[], groupBy: DeckGroupBy, sortBy: SummarySortBy): SummarySectionCard[] {
  return [...cards].sort((a, b) => {
    // If explicit sort preference set, use it
    if (sortBy === 'name') {
      return a.card.name.localeCompare(b.card.name);
    }

    if (sortBy === 'price') {
      const priceA = a.price ?? 0;
      const priceB = b.price ?? 0;
      return priceB - priceA; // Highest price first
    }

    // Default CMC sort with secondary by groupBy context
    if (groupBy === 'cmc') {
      // Within CMC groups, sort by type then name
      const typeA = getTypeSortOrder(a.typeCategory ?? 'other');
      const typeB = getTypeSortOrder(b.typeCategory ?? 'other');
      if (typeA !== typeB) return typeA - typeB;
      return a.card.name.localeCompare(b.card.name);
    } else if (groupBy === 'type' || groupBy === 'color_identity') {
      // Within type/color groups, sort by CMC then name
      const cmcA = getCmc(a.card) ?? 0;
      const cmcB = getCmc(b.card) ?? 0;
      if (cmcA !== cmcB) return cmcA - cmcB;
      return a.card.name.localeCompare(b.card.name);
    }

    return a.card.name.localeCompare(b.card.name);
  });
}

/**
 * Convert deck cards to section cards
 */
function toSectionCards(
  deckCards: EnrichedDeckCard[],
  ownedOracleIds: Set<string> | undefined,
  priceMap: Map<string, number> | undefined,
): SummarySectionCard[] {
  return deckCards.map((dc) => ({
    card: dc.card,
    quantity: dc.quantity,
    price: priceMap?.get(dc.scryfall_id) ?? null,
    isOwned: ownedOracleIds?.has(getOracleId(dc.card) ?? ''),
    typeCategory: dc.classification.type_category,
  }));
}

/**
 * Group mainboard cards based on the selected groupBy setting
 */
function groupMainboardCards(
  cards: EnrichedDeckCard[],
  groupBy: DeckGroupBy,
  ownedOracleIds: Set<string> | undefined,
  priceMap: Map<string, number> | undefined,
  sortBy: SummarySortBy,
): CategoryGroup[] {
  const groups = new Map<string, { label: string; sortOrder: number; cards: SummarySectionCard[] }>();

  for (const dc of cards) {
    // Skip companion zone - handled separately
    if (dc.zone === 'companion') continue;

    let key: string;
    let label: string;
    let sortOrder: number;

    switch (groupBy) {
      case 'type': {
        const category = dc.classification.type_category;
        label = TYPE_CATEGORY_LABELS[category];
        key = category;
        sortOrder = getTypeSortOrder(category);
        break;
      }
      case 'cmc': {
        const cmc = getCmc(dc.card) ?? 0;
        label = getCmcLabel(cmc);
        key = String(Math.min(cmc, 7));
        sortOrder = cmc >= 7 ? 7 : cmc;
        break;
      }
      case 'color_identity':
        label = getColorIdentityGroup(dc.card.color_identity);
        key = label;
        sortOrder = getColorIdentitySortOrder(label);
        break;
      default: {
        const defaultCategory = dc.classification.type_category;
        label = TYPE_CATEGORY_LABELS[defaultCategory];
        key = defaultCategory;
        sortOrder = getTypeSortOrder(defaultCategory);
      }
    }

    const sectionCard: SummarySectionCard = {
      card: dc.card,
      quantity: dc.quantity,
      price: priceMap?.get(dc.scryfall_id) ?? null,
      isOwned: ownedOracleIds?.has(getOracleId(dc.card) ?? ''),
      typeCategory: dc.classification.type_category,
    };

    const existing = groups.get(key);
    if (existing) {
      existing.cards.push(sectionCard);
    } else {
      groups.set(key, { label, sortOrder, cards: [sectionCard] });
    }
  }

  // Convert to CategoryGroup array
  const result: CategoryGroup[] = [];
  for (const [key, group] of groups) {
    const sortedCards = sortCardsInGroup(group.cards, groupBy, sortBy);
    const totalCount = sortedCards.reduce((sum, c) => sum + c.quantity, 0);
    result.push({
      id: key,
      title: group.label,
      cards: sortedCards,
      count: totalCount,
      sortOrder: group.sortOrder,
    });
  }

  // Sort groups by their sort order
  result.sort((a, b) => a.sortOrder - b.sortOrder);
  return result;
}

/**
 * Group deck cards by zone and groupBy preference
 * Returns mainDeckCategories for the mainboard (using groupBy)
 * and zone sections for commander, companion, sideboard, maybeboard, and tokens
 */
function groupAllCards(
  cards: EnrichedDeckCard[],
  commanderCards: EnrichedDeckCard[],
  sideboardCards: EnrichedDeckCard[],
  maybeboardCards: EnrichedDeckCard[],
  tokensCards: EnrichedDeckCard[],
  groupBy: DeckGroupBy,
  ownedOracleIds: Set<string> | undefined,
  priceMap: Map<string, number> | undefined,
  sortBy: SummarySortBy,
): { mainDeckCategories: CategoryGroup[]; zoneSections: CategoryGroup[] } {
  const zoneSections: CategoryGroup[] = [];

  // Add commander section
  const commanderSectionCards = toSectionCards(commanderCards, ownedOracleIds, priceMap);
  if (commanderSectionCards.length > 0) {
    zoneSections.push({
      id: 'commander',
      title: ZONE_SECTION_CONFIG.commander.title,
      cards: sortCardsInGroup(commanderSectionCards, groupBy, sortBy),
      count: commanderSectionCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: ZONE_SECTION_CONFIG.commander.sortOrder,
    });
  }

  // Add companion cards from mainboard
  const companionCards = cards.filter((dc) => dc.zone === 'companion');
  const companionSectionCards = toSectionCards(companionCards, ownedOracleIds, priceMap);
  if (companionSectionCards.length > 0) {
    zoneSections.push({
      id: 'companion',
      title: ZONE_SECTION_CONFIG.companion.title,
      cards: sortCardsInGroup(companionSectionCards, groupBy, sortBy),
      count: companionSectionCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: ZONE_SECTION_CONFIG.companion.sortOrder,
    });
  }

  // Group mainboard cards (excluding companion)
  const mainDeckCategories = groupMainboardCards(cards, groupBy, ownedOracleIds, priceMap, sortBy);

  // Add sideboard section
  const sideboardSectionCards = toSectionCards(sideboardCards, ownedOracleIds, priceMap);
  if (sideboardSectionCards.length > 0) {
    zoneSections.push({
      id: 'sideboard',
      title: ZONE_SECTION_CONFIG.sideboard.title,
      cards: sortCardsInGroup(sideboardSectionCards, groupBy, sortBy),
      count: sideboardSectionCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: ZONE_SECTION_CONFIG.sideboard.sortOrder,
      isZoneSection: true,
    });
  }

  // Add maybeboard section
  const maybeboardSectionCards = toSectionCards(maybeboardCards, ownedOracleIds, priceMap);
  if (maybeboardSectionCards.length > 0) {
    zoneSections.push({
      id: 'maybeboard',
      title: ZONE_SECTION_CONFIG.maybeboard.title,
      cards: sortCardsInGroup(maybeboardSectionCards, groupBy, sortBy),
      count: maybeboardSectionCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: ZONE_SECTION_CONFIG.maybeboard.sortOrder,
      isZoneSection: true,
    });
  }

  // Add tokens section
  const tokensSectionCards = toSectionCards(tokensCards, ownedOracleIds, priceMap);
  if (tokensSectionCards.length > 0) {
    zoneSections.push({
      id: 'tokens',
      title: ZONE_SECTION_CONFIG.tokens.title,
      cards: sortCardsInGroup(tokensSectionCards, groupBy, sortBy),
      count: tokensSectionCards.reduce((sum, c) => sum + c.quantity, 0),
      sortOrder: ZONE_SECTION_CONFIG.tokens.sortOrder,
      isZoneSection: true,
    });
  }

  // Sort zone sections by their sort order
  zoneSections.sort((a, b) => a.sortOrder - b.sortOrder);

  // Separate commander/companion (shown with mainboard) from the sections below it.
  const topZoneSections = zoneSections.filter((z) => !z.isZoneSection);
  const bottomZoneSections = zoneSections.filter((z) => z.isZoneSection);

  // Prepend commander/companion to mainboard groups
  const combinedMainDeck = [...topZoneSections, ...mainDeckCategories];

  return { mainDeckCategories: combinedMainDeck, zoneSections: bottomZoneSections };
}

// ============================================================================
// Component
// ============================================================================

export default function DeckSummaryView({
  cards,
  commanderCards = [],
  sideboardCards = [],
  maybeboardCards = [],
  tokensCards = [],
  onCardClick,
  ownedOracleIds,
  priceMap,
  currency = 'USD',
}: DeckSummaryViewProps): ReactElement {
  // Global groupBy setting
  const groupBy = useAtomValue(deckGroupByAtom);

  // Sort preference (persisted)
  const [sortBy, setSortBy] = useLocalStorage<SummarySortBy>('mtgcraft:summary-sort-by', 'cmc');

  // Collapsed sections (persisted)
  const [collapsedSections, setCollapsedSections] = useLocalStorage<Record<string, boolean>>('mtgcraft:summary-collapsed', {});

  const toggleCollapse = useCallback(
    (sectionId: string) => {
      setCollapsedSections((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    },
    [setCollapsedSections],
  );

  // Group cards using the global groupBy setting
  const { mainDeckCategories, zoneSections } = useMemo(
    () => groupAllCards(cards, commanderCards, sideboardCards, maybeboardCards, tokensCards, groupBy, ownedOracleIds, priceMap, sortBy),
    [cards, commanderCards, sideboardCards, maybeboardCards, tokensCards, groupBy, ownedOracleIds, priceMap, sortBy],
  );

  // Calculate totals (main deck only, excluding sideboard/maybeboard but including commander/companion)
  const mainDeckTotal = useMemo(() => {
    return mainDeckCategories.reduce((sum, cat) => sum + cat.count, 0);
  }, [mainDeckCategories]);

  const sideboardTotal = useMemo(() => {
    return sideboardCards.reduce((sum, c) => sum + c.quantity, 0);
  }, [sideboardCards]);

  const deckAndSideboardPrice = useMemo(() => {
    const inGameCards = [...commanderCards, ...cards, ...sideboardCards];
    return inGameCards.reduce((sum, c) => {
      const unitPrice = priceMap?.get(c.scryfall_id) ?? null;
      return sum + (unitPrice !== null ? unitPrice * c.quantity : 0);
    }, 0);
  }, [commanderCards, cards, sideboardCards, priceMap]);

  const countLabel = sideboardTotal > 0 ? `${mainDeckTotal}+${sideboardTotal} cards` : `${mainDeckTotal} cards`;

  // Handle card click - find deck card ID
  const handleCardClick = (scryfallId: string) => {
    const allCards = [...commanderCards, ...cards, ...sideboardCards, ...maybeboardCards, ...tokensCards];
    const deckCard = allCards.find((c) => c.scryfall_id === scryfallId);
    if (deckCard && onCardClick) {
      onCardClick(scryfallId, deckCard.id);
    }
  };

  // Cycle sort option
  const cycleSortBy = useCallback(() => {
    const options: SummarySortBy[] = ['cmc', 'name', 'price'];
    const currentIdx = options.indexOf(sortBy);
    const nextIdx = (currentIdx + 1) % options.length;
    setSortBy(options[nextIdx]);
  }, [sortBy, setSortBy]);

  const sortLabel = sortBy === 'cmc' ? 'CMC' : sortBy === 'name' ? 'Name' : 'Price';
  const groupByLabel = GROUP_BY_LABELS[groupBy];

  // Determine column count based on window width (max 2 columns)
  const { width } = useWindowSize();
  const lgSize = 1024;
  const columnCount = useMemo(() => {
    if (!width || width < lgSize) return 1;
    return 2;
  }, [width]);

  // Distribute categories to columns using masonry (shortest column first)
  const mainDeckColumns = useMemo(() => distributeToColumns(mainDeckCategories, columnCount), [mainDeckCategories, columnCount]);

  const zoneColumns = useMemo(
    () => distributeToColumns(zoneSections, Math.min(columnCount, zoneSections.length || 1)),
    [zoneSections, columnCount],
  );

  // Render a category section
  const renderCategory = (cat: CategoryGroup, isZoneSection = false) => {
    const isCollapsed = collapsedSections[cat.id] ?? false;
    return (
      <div key={cat.id} className="min-w-0">
        {/* Collapsible header */}
        <button
          type="button"
          onClick={() => toggleCollapse(cat.id)}
          className="mb-1 flex w-full items-center gap-1.5 text-xs font-semibold tracking-wider"
        >
          {isCollapsed ? <FaChevronRight className="h-2.5 w-2.5 text-zinc-500" /> : <FaChevronDown className="h-2.5 w-2.5 text-zinc-500" />}
          <span className={clsx('uppercase', isZoneSection ? 'text-amber-400/80' : 'text-zinc-400')}>{cat.title}</span>
          <span className="text-zinc-600">({cat.count})</span>
        </button>
        {!isCollapsed && (
          <SummarySection
            title=""
            count={cat.count}
            cards={cat.cards}
            showPrices
            onCardClick={handleCardClick}
            hideHeader
            currency={currency}
          />
        )}
      </div>
    );
  };

  return (
    <div className="deck-summary-view mx-auto w-full max-w-full lg:w-fit lg:max-w-none">
      {/* Header with totals and grouping info */}
      <div className="mb-4 flex items-baseline justify-between border-b border-zinc-700 pb-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-sm font-semibold tracking-wider text-zinc-400 uppercase">Deck Summary</h2>
          <span className="text-xs text-zinc-600">Grouped by {groupByLabel}</span>
          <button
            type="button"
            onClick={cycleSortBy}
            className="text-xs text-zinc-500 transition-colors hover:text-zinc-300"
            title="Click to change sort order"
          >
            Sort: {sortLabel}
          </button>
        </div>
        <div className="flex items-baseline gap-4 text-sm">
          <span className="text-zinc-400">
            <span className="font-semibold text-zinc-200">{countLabel}</span>
          </span>
          {deckAndSideboardPrice > 0 && (
            <span className="font-mono text-zinc-400">
              <span className="font-semibold text-zinc-200">{formatPrice(deckAndSideboardPrice, currency)}</span>
            </span>
          )}
        </div>
      </div>

      {/* Main deck - Masonry columns */}
      <div className="flex flex-col gap-6 lg:w-fit lg:flex-row lg:gap-12">
        {mainDeckColumns.map((column, colIdx) => (
          <div key={colIdx} className="flex min-w-0 flex-col gap-4 lg:w-fit lg:min-w-86 lg:flex-none">
            {column.map((cat) => renderCategory(cat, false))}
          </div>
        ))}
      </div>

      {/* Zone sections - always below the main deck */}
      {zoneSections.length > 0 && (
        <div className="mt-6 border-t border-zinc-800 pt-4">
          <div className="flex flex-col gap-6 lg:w-fit lg:flex-row lg:gap-12">
            {zoneColumns.map((column, colIdx) => (
              <div key={colIdx} className="flex min-w-0 flex-col gap-4 lg:w-fit lg:min-w-86 lg:flex-none">
                {column.map((cat) => renderCategory(cat, true))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
